import { BudgetScenarioImpactResult } from '@callmart/types';
import { QueryKey as TQueryKey, useQuery } from '@tanstack/react-query';
import { QueryKey } from '../../enums/QueryKey';
import {
    getScenarioContactValues,
    getScenarioImpact,
    previewScenarioImpact,
    ScenarioImpactPreviewPayload,
} from '../../services/budgetScenarioContactValues';
import { createScenarioQuery, isValidScenarioId } from './budgetScenarios';

export const useScenarioContactValues = createScenarioQuery(
    QueryKey.BudgetScenarioContactValues,
    getScenarioContactValues
);

/** The impact of the UNSAVED draft (CALL-783), so the calculated view answers what is on screen instead of
 *  what was last stored.
 *
 *  A query rather than a mutation, keyed on the payload itself, because the payload is the identity of the
 *  QUESTION: change a cell, a date or the version and a different key asks it again, so an edit can never be
 *  answered by the previous draft's numbers. `enabled` is the whole request-frequency story: the caller only
 *  turns it on for the impact VIEW, where the input grid is not even mounted, so a keystroke cannot reach it.
 *
 *  The payload is NOT the identity of the ANSWER, which is why this query keeps the default staleness. The
 *  impact is the draft applied to a BASELINE, and for a draft version that baseline is live, bucket-global
 *  master data — billing configs, minute prices, contract ABTs — plus the version's forecast volumes, all of
 *  which change without this key changing and none of whose mutations invalidate it (they invalidate the
 *  billing/summary/calculation keys). Only a RELEASED snapshot version is genuinely frozen: its configs are
 *  captured in the snapshot and never extrapolated, so there the payload really does determine the number.
 *  It used to be `staleTime: Infinity`, which meant a preview computed before a colleague changed a price was
 *  reused for the rest of the page's life — and, because the query observer survives the input⇄impact
 *  toggle, refetched never. The persisted impact query beside it self-heals on focus, so the two views of one
 *  section could disagree. Ordinary staleness costs one request per toggle and lets a refocus catch up. */
export function useScenarioImpactPreview(
    scenarioId: number | undefined,
    payload: ScenarioImpactPreviewPayload | null,
    enabled: boolean
): [BudgetScenarioImpactResult | undefined, boolean] {
    const isEnabled = enabled && isValidScenarioId(scenarioId) && payload !== null;
    const { data, isFetching } = useQuery<BudgetScenarioImpactResult, Error>({
        queryKey: [QueryKey.BudgetScenarioImpactPreview, scenarioId, payload] as TQueryKey,
        queryFn: () => previewScenarioImpact(scenarioId!, payload!),
        enabled: isEnabled,
        // A failed preview is not an error the user has to act on: the caller falls back to the last saved
        // impact and says so on the badge, so retrying a 4xx draft rejection would only cost requests.
        retry: false,
    });

    return [isEnabled ? data : undefined, isEnabled && isFetching];
}

/** The LAST SAVED impact. No versionId: it is priced against the version the scenario is stored with,
 *  so an unsaved switch of the Budget select must not re-key it — that is the preview's job above. */
export const useScenarioImpact = createScenarioQuery(QueryKey.BudgetScenarioImpact, getScenarioImpact);
